import React, { useEffect, useState, useCallback } from 'react';
import { createRoot } from 'react-dom/client';
import { get, post } from './api';
import { t, LanguageSwitcher } from '@omnifuse/shared/frontend/i18n';
import { SettingsPanel } from '@omnifuse/shared/frontend/SettingsPanel';
import { get as apiGet, post as apiPost, put as apiPut } from './api';

const S = {
  page: { maxWidth: 1150, margin: '0 auto', padding: 24 } as React.CSSProperties,
  card: { background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,.08)', marginBottom: 16 } as React.CSSProperties,
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 16 } as React.CSSProperties,
  btn: { padding: '6px 14px', borderRadius: 8, border: '1px solid #ccc', background: '#fff', cursor: 'pointer', fontSize: 13 } as React.CSSProperties,
  btnPrimary: { padding: '6px 14px', borderRadius: 8, border: 'none', background: '#1a73e8', color: '#fff', cursor: 'pointer', fontSize: 13 } as React.CSSProperties,
  table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: 13 },
  th: { textAlign: 'left' as const, padding: '8px 10px', borderBottom: '2px solid #eee', color: '#555', whiteSpace: 'nowrap' as const },
  td: { padding: '8px 10px', borderBottom: '1px solid #f0f0f0' },
  tab: (a: boolean) => ({ padding: '8px 18px', cursor: 'pointer', borderBottom: a ? '2px solid #1a73e8' : '2px solid transparent', color: a ? '#1a73e8' : '#555', fontWeight: a ? 600 : 400, fontSize: 14 }) as React.CSSProperties,
};

function scoreColor(score: number): string {
  return score >= 75 ? '#188038' : score >= 50 ? '#e8710a' : '#d93025';
}

// ═══ 缺口扫描 ═══
function GapsView() {
  const [summary, setSummary] = useState<any>(null);
  const [gaps, setGaps] = useState<any[]>([]);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    get('/api/v1/scan/summary').then((r: any) => setSummary(r.data)).catch(() => {});
    get('/api/v1/scan/gaps').then((r: any) => setGaps(r.data || [])).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const scan = async () => {
    setBusy(true);
    try { const r: any = await post('/api/v1/scan'); alert(`扫描 ${r.data.products_scanned} 商品，发现 ${r.data.gtin_issues} 个 GTIN 问题`); load(); }
    catch (e: any) { alert(e.message); }
    setBusy(false);
  };

  const extract = async (pid: string) => {
    try { const r: any = await post(`/api/v1/extract/${encodeURIComponent(pid)}`); alert(`提取完成：${r.data.length} 个来源`); }
    catch (e: any) { alert(e.message); }
  };

  const genSchema = async (pid: string) => {
    try { const r: any = await post(`/api/v1/schema/${encodeURIComponent(pid)}/generate`); alert(`已生成 Schema: ${r.data.schema_types.join(', ')}`); }
    catch (e: any) { alert(e.message); }
  };

  return (
    <>
      {summary && (
        <div style={S.grid}>
          <div style={S.card}><div style={{ fontSize: 12, color: '#888' }}>{t('商品总数')}</div><div style={{ fontSize: 26, fontWeight: 700 }}>{summary.products}</div></div>
          <div style={S.card}><div style={{ fontSize: 12, color: '#888' }}>{t('平均完整度')}</div><div style={{ fontSize: 26, fontWeight: 700, color: scoreColor(summary.avg_completeness) }}>{summary.avg_completeness}%</div></div>
          <div style={S.card}><div style={{ fontSize: 12, color: '#888' }}>{t('严重缺口商品')}</div><div style={{ fontSize: 26, fontWeight: 700, color: '#d93025' }}>{summary.critical_products}</div></div>
          <div style={S.card}><div style={{ fontSize: 12, color: '#888' }}>{t('GTIN 缺失/无效')}</div><div style={{ fontSize: 26, fontWeight: 700 }}>{summary.gtin.missing + summary.gtin.invalid}<span style={{ fontSize: 13, color: '#aaa' }}> / {summary.gtin.total}</span></div></div>
        </div>
      )}
      <div style={{ ...S.card, display: 'flex', gap: 10 }}>
        <button style={S.btnPrimary} disabled={busy} onClick={scan}>{busy ? '扫描中...' : t('🔍 全店扫描')}</button>
      </div>
      <div style={S.card}>
        <table style={S.table}>
          <thead><tr><th style={S.th}>{t('商品')}</th><th style={S.th}>{t('品类')}</th><th style={S.th}>{t('完整度')}</th><th style={S.th}>{t('缺口数')}</th><th style={S.th}>{t('操作')}</th></tr></thead>
          <tbody>
            {gaps.map((g: any) => {
              const items = JSON.parse(g.gaps_json || '[]');
              return (
                <React.Fragment key={g.product_id}>
                  <tr onClick={() => setExpanded(expanded === g.product_id ? null : g.product_id)} style={{ cursor: 'pointer' }}>
                    <td style={S.td}>{g.title}</td>
                    <td style={S.td}>{g.category}</td>
                    <td style={{ ...S.td, fontWeight: 700, color: scoreColor(g.completeness_score) }}>{g.completeness_score}%</td>
                    <td style={S.td}>{items.length}</td>
                    <td style={{ ...S.td, whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                      <span style={{ display: 'inline-flex', gap: 6 }}>
                        <button style={S.btn} onClick={() => extract(g.product_id)}>{t('🤖 AI 提取')}</button>
                        <button style={S.btn} onClick={() => genSchema(g.product_id)}>{t('📋 生成 Schema')}</button>
                      </span>
                    </td>
                  </tr>
                  {expanded === g.product_id && (
                    <tr><td style={{ ...S.td, background: '#fafafa' }} colSpan={5}>
                      {items.map((it: any, i: number) => (
                        <div key={i} style={{ padding: '4px 0', fontSize: 12 }}>
                          <span style={{ color: it.severity === 'required' ? '#d93025' : '#e8710a', fontWeight: 600 }}>
                            [{it.severity === 'required' ? '必填' : t('推荐')}]
                          </span> <strong>{it.label}</strong> — {it.why}
                        </div>
                      ))}
                    </td></tr>
                  )}
                </React.Fragment>
              );
            })}
            {gaps.length === 0 && <tr><td style={S.td} colSpan={5}>{t('暂无数据 — 点击"全店扫描"')}</td></tr>}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ═══ 提取审核 ═══
function ExtractionsView() {
  const [rows, setRows] = useState<any[]>([]);

  const load = () => get('/api/v1/extract?status=draft').then((r: any) => setRows(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const act = async (id: string, action: 'apply' | 'reject') => {
    try {
      const r: any = await post(`/api/v1/extract/${id}/${action}`);
      if (action === 'apply') alert(`已回写 ${r.data.metafields_written} 个 metafield`);
      load();
    } catch (e: any) { alert(e.message); }
  };

  return (
    <div style={S.card}>
      <h3 style={{ fontSize: 15, marginBottom: 12 }}>{t('待审核提取草稿')}</h3>
      {rows.map((r: any) => (
        <div key={r.id} style={{ border: '1px solid #eee', borderRadius: 8, padding: 14, marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <strong>{r.title || r.product_id} <span style={{ color: '#888', fontWeight: 400 }}>（{r.source === 'text' ? '文本提取' : t('图像提取')}）</span></strong>
            <span style={{ display: 'inline-flex', gap: 6 }}>
              <button style={S.btnPrimary} onClick={() => act(r.id, 'apply')}>{t('✓ 回写 Shopify')}</button>
              <button style={S.btn} onClick={() => act(r.id, 'reject')}>{t('✕ 拒绝')}</button>
            </span>
          </div>
          <pre style={{ fontSize: 12, background: '#f8f9fa', padding: 10, borderRadius: 6, whiteSpace: 'pre-wrap', maxHeight: 200, overflow: 'auto' }}>
            {JSON.stringify(JSON.parse(r.extracted_json), null, 2)}
          </pre>
        </div>
      ))}
      {rows.length === 0 && <div style={{ color: '#888' }}>{t('暂无草稿 — 在缺口扫描页对商品执行"AI 提取"')}</div>}
    </div>
  );
}

// ═══ Schema 管理 ═══
function SchemaView() {
  const [rows, setRows] = useState<any[]>([]);

  const load = () => get('/api/v1/schema').then((r: any) => setRows(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const apply = async (id: string) => {
    try { const r: any = await post(`/api/v1/schema/${id}/apply`); alert(`已写入 metafield。${r.data.hint}`); load(); }
    catch (e: any) { alert(e.message); }
  };

  return (
    <div style={S.card}>
      <h3 style={{ fontSize: 15, marginBottom: 12 }}>{t('JSON-LD Schema 生成记录')}</h3>
      <table style={S.table}>
        <thead><tr><th style={S.th}>{t('商品')}</th><th style={S.th}>{t('Schema 类型')}</th><th style={S.th}>{t('状态')}</th><th style={S.th}>{t('时间')}</th><th style={S.th}></th></tr></thead>
        <tbody>
          {rows.map((r: any) => (
            <tr key={r.id}>
              <td style={S.td}>{r.title || r.product_id}</td>
              <td style={S.td}>{r.schema_types}</td>
              <td style={S.td}>{r.status === 'applied' ? '✅ 已注入' : t('已生成')}</td>
              <td style={S.td}>{(r.created_at || '').slice(0, 16)}</td>
              <td style={S.td}>{r.status !== 'applied' && <button style={S.btnPrimary} onClick={() => apply(r.id)}>{t('注入店铺')}</button>}</td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td style={S.td} colSpan={5}>{t('暂无记录')}</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

// ═══ 多渠道 Feed ═══
function FeedsView() {
  const [rows, setRows] = useState<any[]>([]);
  const [busy, setBusy] = useState('');

  const load = () => get('/api/v1/feeds').then((r: any) => setRows(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const gen = async (channel: string) => {
    setBusy(channel);
    try { const r: any = await post(`/api/v1/feeds/${channel}/generate`); alert(`${channel} feed 已生成：${r.data.item_count} 个商品`); load(); }
    catch (e: any) { alert(e.message); }
    setBusy('');
  };

  const shop = new URLSearchParams(window.location.search).get('shop') || localStorage.getItem('dev_shop') || 'dev-shop.myshopify.com';

  return (
    <div style={S.card}>
      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        <button style={S.btnPrimary} disabled={!!busy} onClick={() => gen('google')}>{busy === 'google' ? '生成中...' : 'Google Merchant XML'}</button>
        <button style={S.btnPrimary} disabled={!!busy} onClick={() => gen('meta')}>{busy === 'meta' ? '生成中...' : 'Meta Commerce CSV'}</button>
        <button style={S.btnPrimary} disabled={!!busy} onClick={() => gen('tiktok')}>{busy === 'tiktok' ? '生成中...' : 'TikTok Shop CSV'}</button>
      </div>
      <table style={S.table}>
        <thead><tr><th style={S.th}>{t('渠道')}</th><th style={S.th}>{t('格式')}</th><th style={S.th}>{t('商品数')}</th><th style={S.th}>{t('生成时间')}</th><th style={S.th}></th></tr></thead>
        <tbody>
          {rows.map((r: any) => (
            <tr key={r.id}>
              <td style={S.td}>{r.channel}</td><td style={S.td}>{r.format}</td>
              <td style={S.td}>{r.item_count}</td><td style={S.td}>{(r.generated_at || '').slice(0, 16)}</td>
              <td style={S.td}><a href={`/api/v1/feeds/${r.id}/download?shop=${encodeURIComponent(shop)}`} target="_blank" rel="noreferrer">{t('⬇ 下载')}</a></td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td style={S.td} colSpan={5}>{t('暂无导出记录')}</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

// ═══ 主应用 ═══
function App() {
  const [tab, setTab] = useState('gaps');
  return (
    <div style={S.page}>
      <div style={{ position: 'fixed', top: 12, right: 16, zIndex: 1000 }}><LanguageSwitcher /></div>
      <h1 style={{ fontSize: 22, marginBottom: 4 }}>StructuredFeedOptimizer</h1>
      <p style={{ color: '#666', marginBottom: 16 }}>{t('产品数据结构化与 AI 渠道优化 — 字段缺口 · 多模态提取 · Schema · 多渠道 Feed')}</p>
      <div style={{ display: 'flex', borderBottom: '1px solid #eee', marginBottom: 20 }}>
        <div style={S.tab(tab === 'gaps')} onClick={() => setTab('gaps')}>{t('字段缺口')}</div>
        <div style={S.tab(tab === 'extract')} onClick={() => setTab('extract')}>{t('提取审核')}</div>
        <div style={S.tab(tab === 'schema')} onClick={() => setTab('schema')}>JSON-LD</div>
        <div style={S.tab(tab === 'feeds')} onClick={() => setTab('feeds')}>{t('多渠道 Feed')}</div>
        <div style={S.tab(tab === 'settings')} onClick={() => setTab('settings')}>{t('设置')}</div>
      </div>
      {tab === 'gaps' && <GapsView />}
      {tab === 'extract' && <ExtractionsView />}
      {tab === 'schema' && <SchemaView />}
      {tab === 'feeds' && <FeedsView />}
      {tab === 'settings' && <SettingsPanel api={{ get: apiGet, post: apiPost, put: apiPut }} />}
    </div>
  );
}

const root = createRoot(document.getElementById('root')!);
root.render(<App />);
